//@flow
import React, {useState, useEffect} from 'react';

import type {ConversationMessage} from '../../../../../entities';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {getConversationsService} from '../../promises';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  makeStyles,
  Avatar,
  Typography,
} from '@material-ui/core';

import {getConversationDisplayName} from '../../utils/conversation';
import InlineLoader from '../../../../../elements/LoadingSpinner/InlineLoader';
import {getServiceLabel} from '../../utils';
import {getMultichatServiceIcon} from '../../../utils/service-icon';
import SearchInput from '../../../../../elements/SearchInput';
import {debounce} from '../../../../../utils/functions';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';

import type {
  Conversation,
  MessageType,
  MessageRequest,
} from '../../../../../entities';
import {uploadImageService} from '../../../../file-storage/promises';
import {sendMessageService} from '../../promises/message';
import {useToastActions} from '../../../../toast-alert/utils';

type Props = {
  message: ConversationMessage,
  open: boolean,
  onClose: () => void,
};

type SendMessageParams = {
  message?: string,
  files?: File[] | Blob[],
  messageType?: MessageType,
  attachment?: string,
  conversationId?: string,
};

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  root: {
    width: '100%',
    maxHeight: 500,
    position: 'relative',
    overflow: 'auto',
    minHeight: 200,
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    top: 0,
    left: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  serviceLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.25rem',
  },
  title: {
    fontWeight: 'bold',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  submitButton: {
    width: '100%',
    maxWidth: 200,
  },
}));

function getExtensionFromMime(mime: string, fallback: string) {
  if (!mime) return fallback;
  const [, subtype] = mime.split('/');
  if (!subtype) return fallback;
  return subtype.split(';')[0].trim();
}

export default function ForwardMessageModal(props: Props) {
  const {message, onClose, open} = props;

  const classes = useStyles();
  const [checkedConversationIds, setCheckedConversationIds] = useState([]);

  const {showToast} = useToastActions();

  const handleToggle = value => {
    const currentIndex = checkedConversationIds.indexOf(value.id);
    const newChecked = [...checkedConversationIds];

    if (currentIndex === -1) {
      newChecked.push(value.id);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCheckedConversationIds(newChecked);
  };

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getConversationsService(
      ['FB_MESSENGER', 'WHATSAPP_WEB', 'INSTAGRAM', 'WHATSAPP_API'],
      {
        pagination: {
          page: 0,
          size: 15,
        },
      }
    )
      .then(res => {
        setConversations(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const forwardMessage = async ({
    conversationId,
    files,
    message,
    messageType,
    attachment,
  }: SendMessageParams) => {
    if (!conversationId) return;

    if (!files && !message) {
      return;
    }

    const newMessage: MessageRequest = {
      text: message,
      messageType: messageType || 'TEXT',
    };

    if (files && files.length > 0) {
      const [fileResult] = await uploadImageService(
        files,
        'im/crm-conversation',
        {
          temp: true,
        }
      );
      newMessage.fileUrl = fileResult.url;
    }

    if (attachment) {
      newMessage.fileUrl = attachment;
    }

    await sendMessageService(newMessage, conversationId);
  };

  const handleForward = async () => {
    setLoading(true);
    try {
      const promises = checkedConversationIds.map(conversationId => {
        const request: SendMessageParams = {
          conversationId,
          message: message.text,
          messageType: message.messageType,
        };

        let mediaSrc: string | Blob | File | typeof undefined = message.text;
        if (message.messageType === 'IMAGE') {
          const [images] = message.images || [];
          if (images?.src) {
            mediaSrc = images?.src;
          }
        }

        if (message.messageType === 'VIDEO') {
          const [videos] = message.videos || [];
          if (videos?.src) {
            mediaSrc = videos?.src;
          }
        }

        if (message.messageType === 'AUDIO') {
          const [audios] = message.audios || [];

          if (audios?.src && audios?.mimetype) {
            const extension = getExtensionFromMime(audios.mimetype, 'mp3');
            const filename = `audio.${extension}`;

            const file = new File([audios.src], filename, {
              type: audios.mimetype || 'audio/mpeg',
            });

            mediaSrc = file;
          }
        }

        if (message.messageType === 'DOCUMENT') {
          const [documents] = message.documents || [];
          if (documents?.src) {
            mediaSrc = documents?.src;
          }
        }

        if (typeof mediaSrc === 'string' && message.messageType !== 'TEXT') {
          request.attachment = mediaSrc;
        }

        if (typeof mediaSrc === 'object' && message.messageType !== 'TEXT') {
          request.files = [mediaSrc];
        }

        return forwardMessage(request);
      });

      await Promise.all(promises);
      setCheckedConversationIds([]);
      onClose();
    } catch (error) {
      console.error(error);
      showToast({
        title: 'Error al reenviar el mensaje',
        message:
          'Ocurrio un error al reenviar el mensaje, por favor intenta de nuevo',
        type: 'danger',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = debounce((value: string) => {
    const parsedValue = value.trim();
    setLoading(true);
    getConversationsService(
      ['FB_MESSENGER', 'WHATSAPP_WEB', 'INSTAGRAM', 'WHATSAPP_API'],
      {
        pagination: {
          page: 0,
          size: 15,
        },
        identifier: parsedValue.length > 0 ? parsedValue : undefined,
      }
    )
      .then(res => {
        setConversations(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, 500);

  return (
    <CustomModal open={open} handleCloseModal={onClose}>
      <div className={classes.modal}>
        <Typography className={classes.title} align="center">
          Reenviar mensage
        </Typography>

        <SearchInput
          placeholder="Buscar"
          onChange={handleChange}
          className={classes.searchInput}
        />

        <List dense className={classes.root}>
          {loading && (
            <div className={classes.loader}>
              <InlineLoader loading size={40} />
            </div>
          )}
          {conversations.map(conversation => {
            const displayName = getConversationDisplayName(conversation);
            return (
              <ListItem key={conversation.id}>
                <ListItemAvatar>
                  <Avatar>{displayName.substring(0, 2).toUpperCase()}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={displayName}
                  secondary={
                    <div className={classes.serviceLabel}>
                      <img
                        src={getMultichatServiceIcon(conversation.ravenService)}
                        alt={conversation.ravenService}
                        width={20}
                        height={20}
                      />
                      <Typography variant="caption">
                        {getServiceLabel(conversation.ravenService)}
                      </Typography>
                    </div>
                  }
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge="end"
                    color="primary"
                    checked={
                      checkedConversationIds.indexOf(conversation.id) !== -1
                    }
                    onChange={() => handleToggle(conversation)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>

        <footer className={classes.footer}>
          <CustomButton
            onClick={handleForward}
            variant="contained"
            color="primary"
            className={classes.submitButton}
          >
            Reenviar
          </CustomButton>
        </footer>
      </div>
    </CustomModal>
  );
}
